import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip,
} from 'recharts';
import { Tooltip as MuiTooltip } from '@mui/material';
import ClipLoader from 'react-spinners/ClipLoader';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useFetch } from './api';
import HomepageActivityFeed from './HomepageActivityFeed';

dayjs.extend(utc);

const chartAxisStyle = {
  fontSize: '11px',
  fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
  fill: '#A8A29E',
};

const chartGridStyle = {
  stroke: '#F0EEEC',
  strokeDasharray: '4 4',
};

const SECTION_LABEL = 'text-[11px] font-semibold text-surface-400 uppercase tracking-wider';

// compact axis labels (1200 -> 1.2K) so wide counts don't clip the y-axis
const compactNumber = new Intl.NumberFormat('en-US', { notation: 'compact' });

const STATUS_LABELS = {
  400: 'Bad request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not found',
  409: 'Conflict',
  422: 'Unprocessable entity',
  429: 'Rate limited',
  500: 'Server error',
  502: 'Bad gateway',
  503: 'Service unavailable',
  504: 'Gateway timeout',
};

function gaugeColor(value) {
  if (value >= 95) return '#10B981';
  if (value >= 90) return '#F59E0B';
  return '#EF4444';
}

function GaugeMeter({ value, label, tooltip }) {
  const p = value == null ? 0 : Math.max(0, Math.min(100, value));
  const arc = 'M 8 66 A 56 56 0 0 1 120 66';
  return (
    <div className="relative flex-shrink-0" style={{ width: 128, height: 74 }}>
      <svg width={128} height={74} viewBox="0 0 128 74" fill="none">
        <path d={arc} stroke="#F0EEEC" strokeWidth={9} strokeLinecap="round" pathLength={100} />
        {p > 0 ? (
          <path
            d={arc}
            stroke={gaugeColor(p)}
            strokeWidth={9}
            strokeLinecap="round"
            pathLength={100}
            strokeDasharray={100}
            strokeDashoffset={100 - p}
            style={{ transition: 'stroke-dashoffset 1s cubic-bezier(0.4,0,0.2,1)' }}
          />
        ) : null}
      </svg>
      <div className="absolute inset-x-0 bottom-0 flex flex-col items-center">
        <span className="font-mono text-[22px] font-bold text-surface-900 tracking-tight leading-none">
          {value == null ? '—' : `${value.toFixed(1)}%`}
        </span>
        <span className="text-[10px] font-medium text-surface-400 mt-1 uppercase tracking-wider">{label}</span>
      </div>
      {tooltip ? (
        <MuiTooltip title={tooltip} arrow placement="top">
          <svg className="absolute top-0 right-0 w-3.5 h-3.5 text-surface-300 hover:text-surface-500 cursor-help transition-colors" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10A8 8 0 11 2 10a8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        </MuiTooltip>
      ) : null}
    </div>
  );
}

function Stat({
  label, value, valueClass, to,
}) {
  const inner = (
    <>
      <p className="text-[11px] font-semibold text-surface-400 uppercase tracking-wider mb-2">{label}</p>
      <p className={`font-mono text-2xl font-semibold tracking-tight leading-none ${valueClass || 'text-surface-900'}`}>
        {value}
      </p>
    </>
  );
  if (to) {
    return <Link to={to} className="flex flex-col justify-center hover:opacity-80 transition-opacity">{inner}</Link>;
  }
  return <div className="flex flex-col justify-center">{inner}</div>;
}

function SectionHeader({ title, right }) {
  return (
    <div className="flex justify-between items-baseline mb-3">
      <h2 className="text-sm font-semibold text-surface-800">{title}</h2>
      {right}
    </div>
  );
}

function HeaderLink({ to, children }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-1 text-xs font-medium text-accent-600 hover:text-accent-700 transition-colors group/nav"
    >
      {children}
      <svg className="w-3 h-3 transition-transform group-hover/nav:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14m0 0l-5-5m5 5l-5 5" />
      </svg>
    </Link>
  );
}

function TrafficLegend() {
  return (
    <div className="flex items-center gap-3 text-[11px] font-medium text-surface-400">
      <span className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-success" />
        Success
      </span>
      <span className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-error" />
        Errors
      </span>
    </div>
  );
}

function Chevron() {
  return (
    <svg className="w-4 h-4 text-surface-300 group-hover:text-surface-500 flex-shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

function DayTooltip({ active, payload, unit }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <div className="chart-tooltip">
      <p className="text-xs font-medium text-surface-500 mb-1.5">{dayjs.utc(row.date).format('MMM D')}</p>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="flex items-center gap-2 text-sm py-0.5">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="font-semibold text-surface-800 font-mono">{entry.value.toLocaleString('en-US')}</span>
          <span className="text-surface-400">{unit[entry.dataKey] || entry.dataKey}</span>
        </div>
      ))}
    </div>
  );
}

function HomepageDashboard({
  connectionData, connectionsLoading, integrationLists, days,
}) {
  const trackstar = useFetch();

  const [apiDaily, setApiDaily] = useState([]);
  const [successRate, setSuccessRate] = useState(null);
  const [totalCalls, setTotalCalls] = useState(0);
  const [totalErrors, setTotalErrors] = useState(0);
  const [topErrors, setTopErrors] = useState([]);
  const [loadingAPI, setLoadingAPI] = useState(true);

  const [installRate, setInstallRate] = useState(null);
  const [loadingInstall, setLoadingInstall] = useState(true);

  const getAPIData = (windowDays, isCurrent) => {
    setLoadingAPI(true);
    const now = dayjs().utc();
    const start = now.subtract(windowDays, 'day');
    const url = `internal/api-logs-breakdown?start_date=${start.format('YYYY-MM-DDTHH:mm:ss[Z]')}&end_date=${now.format('YYYY-MM-DDTHH:mm:ss[Z]')}&groups=day,status_code`;

    trackstar(url, 'GET').then((response) => {
      // ignore a response that resolved after the range changed again
      if (!isCurrent()) return;
      const { data } = response;
      const successByDay = {};
      const errorByDay = {};
      const byStatus = {};
      let total = 0;
      let successCount = 0;

      data.forEach((entry) => {
        const status = entry.status_code;
        const key = dayjs.utc(entry.request_time).startOf('day').toISOString();
        total += entry.count;
        byStatus[status] = (byStatus[status] || 0) + entry.count;
        if (status >= 200 && status < 300) {
          successCount += entry.count;
          successByDay[key] = (successByDay[key] || 0) + entry.count;
        } else {
          errorByDay[key] = (errorByDay[key] || 0) + entry.count;
        }
      });

      const series = [];
      for (let i = windowDays - 1; i >= 0; i -= 1) {
        const day = now.subtract(i, 'day').startOf('day');
        const key = day.toISOString();
        series.push({ date: key, success: successByDay[key] || 0, error: errorByDay[key] || 0 });
      }

      const errList = [];
      Object.entries(byStatus).forEach(([status, count]) => {
        const code = Number(status);
        if (code < 200 || code >= 300) errList.push({ status: code, count });
      });
      errList.sort((a, b) => b.count - a.count);

      setApiDaily(series);
      setSuccessRate(total > 0 ? (successCount / total) * 100 : null);
      setTotalCalls(total);
      setTotalErrors(total - successCount);
      setTopErrors(errList.slice(0, 6));
      setLoadingAPI(false);
    }).catch(() => {
      if (!isCurrent()) return;
      // clear the prior range's numbers so a failed refresh shows the empty
      // state, not stale metrics under the new range's label
      setApiDaily([]);
      setSuccessRate(null);
      setTotalCalls(0);
      setTotalErrors(0);
      setTopErrors([]);
      setLoadingAPI(false);
    });
  };

  const getInstallRate = (windowDays, isCurrent) => {
    setLoadingInstall(true);
    const start = dayjs().utc().subtract(windowDays, 'day').toISOString();
    const end = dayjs().utc().toISOString();
    const createdUrl = `internal/events?start_date=${start}&end_date=${end}&type=connection_created`;
    const failedUrl = `internal/events?start_date=${start}&end_date=${end}&type=install_failed`;

    Promise.all([trackstar(createdUrl), trackstar(failedUrl)]).then(([createdRsp, failedRsp]) => {
      if (!isCurrent()) return;
      const created = createdRsp && Array.isArray(createdRsp.data) ? createdRsp.data.length : 0;
      const failed = failedRsp && Array.isArray(failedRsp.data) ? failedRsp.data.length : 0;
      const attempts = created + failed;
      setInstallRate(attempts > 0 ? (created / attempts) * 100 : null);
      setLoadingInstall(false);
    }).catch(() => {
      if (!isCurrent()) return;
      setInstallRate(null);
      setLoadingInstall(false);
    });
  };

  useEffect(() => {
    // guard against a slower earlier-range request overwriting the current one
    let cancelled = false;
    const isCurrent = () => !cancelled;
    getAPIData(days, isCurrent);
    getInstallRate(days, isCurrent);
    return () => { cancelled = true; };
  }, [days]);

  // count connections whose error blocks a sync — matches the Connections
  // "Errors" status the credential-issues link filters to
  let attentionCount = 0;
  connectionData.forEach((conn) => {
    if (conn.errors && conn.errors.length > 0) {
      let blocking = false;
      conn.errors.forEach((err) => {
        if (err.affected_endpoints && err.affected_endpoints.some((ep) => ep.preventing_sync)) {
          blocking = true;
        }
      });
      if (blocking) attentionCount += 1;
    }
  });

  // cumulative connections per day across the selected window
  const today = new Date();
  const growthSeries = [];
  for (let i = days - 1; i >= 0; i -= 1) {
    const day = new Date(today);
    day.setDate(today.getDate() - i);
    let count = 0;
    connectionData.forEach((conn) => {
      if (new Date(conn.created_at) <= day) count += 1;
    });
    growthSeries.push({ date: day.toISOString(), count });
  }

  const hasTraffic = apiDaily.some((d) => d.success > 0 || d.error > 0);
  const attentionClass = attentionCount > 0 ? 'text-error-dark' : 'text-surface-900';

  let errorsBody;
  if (loadingAPI) {
    errorsBody = (
      <div className="flex-1 flex items-center justify-center">
        <ClipLoader size={20} color="#D4532A" />
      </div>
    );
  } else if (topErrors.length) {
    errorsBody = (
      <div className="flex-1 overflow-y-auto -mr-2 pr-2">
        {topErrors.map((err) => (
          <Link key={err.status} to={`/api-log?status_code=${err.status}`} className="flex items-center gap-3 py-2.5 group">
            <span className="badge badge-error tabular-nums flex-shrink-0">{err.status}</span>
            <span className="min-w-0 text-sm text-surface-600 group-hover:text-surface-900 transition-colors truncate">
              {STATUS_LABELS[err.status] || 'Error'}
            </span>
            <span className="text-sm font-semibold text-surface-800 tabular-nums font-mono flex-shrink-0">
              {err.count.toLocaleString('en-US')}
            </span>
            <div className="flex-1" />
            <Chevron />
          </Link>
        ))}
      </div>
    );
  } else {
    errorsBody = (
      <div className="flex-1 flex items-center">
        <p className="text-sm text-surface-500">No errors in this period.</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex gap-6 min-h-0 min-w-0">
      {/* Connections domain */}
      <div className="card p-5 flex-1 flex flex-col min-w-0 min-h-0">
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <div className="flex items-center gap-2">
            <p className={SECTION_LABEL}>Connection health</p>
            {connectionsLoading ? <ClipLoader size={11} color="#A8A29E" /> : null}
          </div>
          <div className="flex items-center gap-4">
            <HeaderLink to="/connections">All connections</HeaderLink>
            <HeaderLink to="/connections?status=Errors">Erroring</HeaderLink>
          </div>
        </div>

        <div className="flex items-center gap-6 pb-5 mb-5 border-b border-surface-100 flex-shrink-0">
          <GaugeMeter
            value={loadingInstall ? null : installRate}
            label="install success"
            tooltip="Successful installs divided by all install attempts in this range: connections created vs failed installs."
          />
          <div className="w-px self-stretch bg-surface-200" />
          <div className="flex divide-x divide-surface-200">
            <div className="pr-6">
              <Stat label="Active" value={connectionData.length.toLocaleString('en-US')} />
            </div>
            <div className="pl-6">
              <Stat label="Credential issues" value={attentionCount.toLocaleString('en-US')} valueClass={attentionClass} />
            </div>
          </div>
        </div>

        <div className="flex flex-col flex-shrink-0 pb-6 mb-6 border-b border-surface-100">
          <SectionHeader title="Connection Growth" />
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={growthSeries}
                margin={{
                  top: 8, right: 4, left: 0, bottom: 0,
                }}
              >
                <defs>
                  <linearGradient id="growthGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#E86F42" stopOpacity={0.22} />
                    <stop offset="100%" stopColor="#E86F42" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  vertical={false}
                  stroke={chartGridStyle.stroke}
                  strokeDasharray={chartGridStyle.strokeDasharray}
                />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ ...chartAxisStyle, dy: 4 }}
                  minTickGap={44}
                  tickFormatter={(v) => dayjs.utc(v).format('MMM D')}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={chartAxisStyle}
                  width={32}
                  allowDecimals={false}
                />
                <Tooltip content={<DayTooltip unit={{ count: 'connections' }} />} cursor={{ stroke: '#E86F42', strokeWidth: 1, strokeDasharray: '4 4' }} />
                <Area type="monotone" dataKey="count" stroke="#E86F42" strokeWidth={2} fill="url(#growthGradient)" isAnimationActive animationDuration={700} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <HomepageActivityFeed integrationLists={integrationLists} days={days} />
      </div>

      {/* API domain */}
      <div className="card p-5 flex-1 flex flex-col min-w-0 min-h-0">
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <p className={SECTION_LABEL}>API health</p>
          <div className="flex items-center gap-4">
            <HeaderLink to="/api-log">All logs</HeaderLink>
            <HeaderLink to="/api-log?errors=true">Errors</HeaderLink>
          </div>
        </div>

        <div className="flex items-center gap-6 pb-5 mb-5 border-b border-surface-100 flex-shrink-0">
          <GaugeMeter value={loadingAPI ? null : successRate} label="2xx success" />
          <div className="w-px self-stretch bg-surface-200" />
          <div className="flex divide-x divide-surface-200">
            <div className="pr-6">
              <Stat label="API calls" value={loadingAPI ? '—' : totalCalls.toLocaleString('en-US')} />
            </div>
            <div className="pl-6">
              <Stat label="Errors" value={loadingAPI ? '—' : totalErrors.toLocaleString('en-US')} valueClass={totalErrors > 0 ? 'text-error-dark' : 'text-surface-900'} />
            </div>
          </div>
        </div>

        <div className="flex flex-col flex-shrink-0 pb-6 mb-6 border-b border-surface-100">
          <SectionHeader title="API Traffic" right={<TrafficLegend />} />
          <div className="h-52">
            {loadingAPI ? (
              <div className="h-full flex items-center justify-center">
                <ClipLoader size={24} color="#D4532A" />
              </div>
            ) : null}
            {!loadingAPI && !hasTraffic ? (
              <div className="h-full flex items-center justify-center text-center">
                <p className="text-sm text-surface-500">No API traffic in this period.</p>
              </div>
            ) : null}
            {!loadingAPI && hasTraffic ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={apiDaily}
                  margin={{
                    top: 8, right: 4, left: 0, bottom: 0,
                  }}
                >
                  <defs>
                    <linearGradient id="successGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.2} />
                      <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="errorGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#EF4444" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#EF4444" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    vertical={false}
                    stroke={chartGridStyle.stroke}
                    strokeDasharray={chartGridStyle.strokeDasharray}
                  />
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ ...chartAxisStyle, dy: 4 }}
                    minTickGap={44}
                    tickFormatter={(v) => dayjs.utc(v).format('MMM D')}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={chartAxisStyle}
                    width={40}
                    allowDecimals={false}
                    tickFormatter={(v) => compactNumber.format(v)}
                  />
                  <Tooltip content={<DayTooltip unit={{ success: 'success', error: 'errors' }} />} cursor={{ stroke: '#A8A29E', strokeWidth: 1, strokeDasharray: '4 4' }} />
                  <Area type="monotone" dataKey="success" stroke="#10B981" strokeWidth={1.75} fill="url(#successGradient)" isAnimationActive animationDuration={700} />
                  <Area type="monotone" dataKey="error" stroke="#EF4444" strokeWidth={1.75} fill="url(#errorGradient)" isAnimationActive animationDuration={700} />
                </AreaChart>
              </ResponsiveContainer>
            ) : null}
          </div>
        </div>

        <div className="flex-1 min-h-0 flex flex-col">
          <SectionHeader title="Top errors" />
          {errorsBody}
        </div>
      </div>
    </div>
  );
}

export default HomepageDashboard;
